import { z } from "./utils/zod";

const DEFAULT_MAX_ATTRIBUTE_VALUE_LENGTH = 2000;
const DEFAULT_MAX_ATTRIBUTE_KEY_LENGTH = 100;
const DEFAULT_MAX_ATTRIBUTE_ARRAY_LENGTH = 1000;

type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

const JsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(JsonValueSchema),
    z.record(z.string(), JsonValueSchema),
  ]),
);

export const EntityAttributesSchema = z
  .record(z.string(), JsonValueSchema)
  .optional()
  .nullable()
  .meta({
    id: "entityAttributesInput",
    description:
      "Entity attributes. Values are normalized exactly like ingestion before being merged with existing attributes.",
  });

export type EntityAttributesInput = Record<string, JsonValue>;
type NormalizedEntityAttributes = Record<string, string | string[]>;

type NormalizeEntityAttributesOptions = {
  maxValueLength?: number;
  maxKeyLength?: number;
  maxArrayLength?: number;
  onArray?: () => void;
  onValueTruncated?: (details: { key: string; length: number }) => void;
  onKeyTruncated?: (details: { key: string; length: number }) => void;
};

const reservedKeys = new Set(["__proto__", "constructor", "prototype"]);

export function isReservedAttributeKey(key: string): boolean {
  // Dotted context paths are expanded during evaluation, and storage removes
  // null bytes. Check the resulting path segments too.
  return key
    .replaceAll("\0", "")
    .split(".")
    .some((part) => reservedKeys.has(part));
}

function removeReservedKeys(value: JsonValue): JsonValue {
  if (Array.isArray(value)) return value.map(removeReservedKeys);
  if (value === null || typeof value !== "object") return value;

  const sanitized: Record<string, JsonValue> = Object.create(null);
  for (const [key, entry] of Object.entries(value)) {
    if (!isReservedAttributeKey(key)) {
      sanitized[key] = removeReservedKeys(entry);
    }
  }
  return sanitized;
}

function normalizeArrayElement(value: JsonValue): string {
  if (value === null) return "";
  if (typeof value !== "object") return String(value);
  return JSON.stringify(value) ?? "";
}

/**
 * Normalize entity attributes into the same representation accepted by the
 * ingestion pipeline: scalar values become strings and arrays become arrays
 * of normalized strings.
 */
export function normalizeEntityAttributes(
  attributes: EntityAttributesInput | null | undefined,
  options: NormalizeEntityAttributesOptions = {},
): NormalizedEntityAttributes {
  if (!attributes) return {};

  const maxValueLength =
    options.maxValueLength ?? DEFAULT_MAX_ATTRIBUTE_VALUE_LENGTH;
  const maxKeyLength = options.maxKeyLength ?? DEFAULT_MAX_ATTRIBUTE_KEY_LENGTH;
  const maxArrayLength =
    options.maxArrayLength ?? DEFAULT_MAX_ATTRIBUTE_ARRAY_LENGTH;
  const normalized: NormalizedEntityAttributes = Object.create(null);

  for (const [key, rawValue] of Object.entries(attributes)) {
    if (isReservedAttributeKey(key)) continue;

    let normalizedValue: string | string[];

    if (Array.isArray(rawValue) && rawValue.length > maxArrayLength) {
      options.onValueTruncated?.({ key, length: rawValue.length });
      normalizedValue = "[TRUNCATED]";
    } else {
      const value = removeReservedKeys(rawValue);
      if (Array.isArray(value)) {
        normalizedValue = value.map(normalizeArrayElement);
        options.onArray?.();
      } else {
        switch (typeof value) {
          case "number":
          case "boolean":
          case "symbol":
          case "bigint":
            normalizedValue = String(value);
            break;
          case "string":
            normalizedValue = value;
            break;
          default: {
            if (value === null) {
              normalizedValue = "";
            } else {
              const serialized = JSON.stringify(value);
              if (serialized === undefined) continue;
              normalizedValue = serialized;
            }
            // Skip empty objects, preserving ingestion behavior.
            if (normalizedValue === "{}") continue;
            break;
          }
        }
      }

      const serializedValue = Array.isArray(normalizedValue)
        ? JSON.stringify(normalizedValue)
        : normalizedValue;
      if (serializedValue.length > maxValueLength) {
        options.onValueTruncated?.({ key, length: serializedValue.length });
        normalizedValue = "[TRUNCATED]";
      }
    }

    let normalizedKey = key;
    if (normalizedKey.length > maxKeyLength) {
      normalizedKey = `${key.slice(0, maxKeyLength - 3)}...`;
      options.onKeyTruncated?.({ key: normalizedKey, length: key.length });
    }

    if (!isReservedAttributeKey(normalizedKey)) {
      normalized[normalizedKey] = normalizedValue;
    }
  }

  return normalized;
}

function sanitizeEntityText(value: string, maxLength = Infinity): string {
  return value.replaceAll("\0", "").slice(0, maxLength);
}

/** Remove null bytes exactly as the ingestion storage writer does for JSONB. */
function sanitizeEntityAttributes(
  attributes: NormalizedEntityAttributes,
): NormalizedEntityAttributes {
  const sanitized: NormalizedEntityAttributes = Object.create(null);

  for (const [key, value] of Object.entries(attributes)) {
    const sanitizedKey = sanitizeEntityText(key);
    sanitized[sanitizedKey] = Array.isArray(value)
      ? value.map((entry) => sanitizeEntityText(entry))
      : sanitizeEntityText(value);
  }

  return sanitized;
}

function getFirstStringAttribute(
  attributes: NormalizedEntityAttributes,
  ...keys: string[]
): string | undefined {
  for (const key of keys) {
    const value = attributes[key];
    if (typeof value === "string" && value.length > 0) return value;
  }
  return undefined;
}

type PrepareEntityUpsertInput = {
  attributes?: EntityAttributesInput | null;
  name?: string;
};

type PreparedEntityUpsert = {
  attributes: NormalizedEntityAttributes;
  name: string | null;
  avatarUrl: string | null;
  nameWasProvided: boolean;
  avatarWasProvided: boolean;
};

function prepareNormalizedEntityUpsert(
  input: PrepareEntityUpsertInput,
  normalizedAttributes: NormalizedEntityAttributes,
): PreparedEntityUpsert {
  const attributeName = getFirstStringAttribute(
    normalizedAttributes,
    "name",
    "$name",
    "Name",
  );
  const attributeIcon = getFirstStringAttribute(
    normalizedAttributes,
    "avatar",
    "$avatar",
    "Avatar",
    "avatar_url",
    "avatarUrl",
  );
  const name = sanitizeEntityText(input.name ?? attributeName ?? "", 128);
  const avatarUrl = sanitizeEntityText(attributeIcon ?? "") || null;
  const attributes = sanitizeEntityAttributes(normalizedAttributes);

  if (input.name !== undefined) attributes.name = name;

  return {
    attributes,
    name: name || null,
    avatarUrl,
    nameWasProvided: name.length > 0,
    avatarWasProvided: avatarUrl !== null,
  };
}

export function prepareEntityUpsert(
  input: PrepareEntityUpsertInput,
): PreparedEntityUpsert {
  const normalizedAttributes = normalizeEntityAttributes(input.attributes);
  return prepareNormalizedEntityUpsert(input, normalizedAttributes);
}

export function prepareUserUpsert(
  input: PrepareEntityUpsertInput,
): PreparedEntityUpsert & {
  email: string | null;
  emailWasProvided: boolean;
} {
  const normalizedAttributes = normalizeEntityAttributes(input.attributes);
  const prepared = prepareNormalizedEntityUpsert(input, normalizedAttributes);
  const email = sanitizeEntityText(
    getFirstStringAttribute(normalizedAttributes, "email", "$email", "Email") ??
      "",
    128,
  );

  return {
    ...prepared,
    email: email || null,
    emailWasProvided: email.length > 0,
  };
}
